import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../components/ui/dialog';
import {
  ArrowRight,
  Shield,
  CheckCircle,
  Building2,
  Award,
  Landmark,
  Mail,
  Phone,
  AlertTriangle,
  IndianRupee,
  Receipt,
  Users,
  FileText,
  ExternalLink
} from 'lucide-react';

const positions = [
  {
    label: 'Not yet a company or LLP',
    icon: Building2,
    paras: [
      "You're pre-registration. Incorporate first — private limited company or LLP, whichever fits the business. Startup India recognition and MP Startup registration both sit behind that step.",
      'We handle incorporation start to finish: company or LLP, PAN, and GST registration.'
    ],
    cta: 'Talk to us about incorporation'
  },
  {
    label: 'Registered, not yet on Startup India',
    icon: Award,
    paras: [
      'You have the company or LLP. Everything on this page is out of reach until DPIIT recognition comes through.',
      "The application takes a few weeks. It doesn't need revenue or profit — it rests on an innovation narrative, and we write and file that narrative for you."
    ],
    cta: 'Talk to us about DPIIT filing'
  },
  {
    label: 'On Startup India, not yet MP Startup',
    icon: Landmark,
    paras: [
      'You cleared the harder step already. MP Startup registration is a shorter form on top of what you have: your DPIIT number plus a Madhya Pradesh GSTIN.',
      "It's the difference between holding Startup India status on paper and drawing actual cash — seed fund, lease rental assistance, advertising reimbursement, event support. Most founders at this stage simply haven't been told the second form exists."
    ],
    cta: 'Talk to us about MP Startup registration'
  }
];

const POSITION_OPTIONS = positions.map((p) => p.label);

const EligibilityDialog = ({ open, onOpenChange, defaultPosition }) => {
  const emptyForm = { name: '', phone: '', email: '', position: '', message: '' };
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm, position: defaultPosition || '' });
      setSubmitted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultPosition]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error('Please enter your name.');
      return;
    }
    if (!form.phone.trim() || form.phone.trim().length < 7) {
      toast.error('Please enter a valid phone number.');
      return;
    }
    if (!form.position) {
      toast.error('Please select where you stand today.');
      return;
    }
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        position: form.position,
        message: form.message.trim(),
        source: 'mp-startup-benefits'
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Something went wrong. Please try again or call +91 75 7004 1003.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-slate-900 border-slate-200 sm:max-w-md" data-testid="eligibility-dialog">
        {submitted ? (
          <div className="py-6 text-center" data-testid="eligibility-success">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 mb-2">Request received</h3>
            <p className="text-sm text-slate-500 max-w-sm mx-auto">
              Thank you, {form.name.split(' ')[0]}. We'll call you within one working day
              with exactly what you qualify for.
            </p>
            <Button
              data-testid="eligibility-close-btn"
              onClick={() => onOpenChange(false)}
              className="mt-6 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Done
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-slate-900 font-medium">Free eligibility check</DialogTitle>
              <DialogDescription className="text-slate-500">
                Tell us where you stand — we'll call you back with exactly what you qualify for.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div>
                <label className="text-sm text-slate-600 mb-1.5 block">Your name *</label>
                <Input
                  data-testid="eligibility-name-input"
                  value={form.name}
                  onChange={set('name')}
                  placeholder="Full name"
                  className="bg-white border-slate-200 text-slate-900"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600 mb-1.5 block">Phone *</label>
                <Input
                  data-testid="eligibility-phone-input"
                  value={form.phone}
                  onChange={set('phone')}
                  placeholder="+91"
                  type="tel"
                  className="bg-white border-slate-200 text-slate-900"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600 mb-1.5 block">Email *</label>
                <Input
                  data-testid="eligibility-email-input"
                  value={form.email}
                  onChange={set('email')}
                  placeholder="you@company.com"
                  type="email"
                  className="bg-white border-slate-200 text-slate-900"
                />
              </div>
              <div>
                <label className="text-sm text-slate-600 mb-1.5 block">Where do you stand today? *</label>
                <select
                  data-testid="eligibility-position-select"
                  value={form.position}
                  onChange={set('position')}
                  className="w-full h-10 px-3 text-sm bg-white border border-slate-200 rounded-md text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select your position</option>
                  {POSITION_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-slate-600 mb-1.5 block">Anything else? (optional)</label>
                <Textarea
                  data-testid="eligibility-message-input"
                  value={form.message}
                  onChange={set('message')}
                  placeholder="Tell us about your startup..."
                  rows={2}
                  className="bg-white border-slate-200 text-slate-900"
                />
              </div>
              <Button
                type="submit"
                data-testid="eligibility-submit-btn"
                disabled={submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-5"
              >
                {submitting ? 'Sending...' : 'Request my free eligibility check'}
                {!submitting && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
              <p className="text-xs text-slate-400 text-center">
                Prefer to talk now? Call <a href="tel:+917570041003" className="text-emerald-600">+91 75 7004 1003</a>
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const dpiitUnlocks = [
  '100% profit tax exemption for any 3 consecutive years in the first 10, under Section 80-IAC, for companies incorporated before 1 April 2030 and cleared by the Inter-Ministerial Board',
  'Self-certification under 6 labour laws and 3 environment laws, with no inspection for 5 years',
  'Fast-tracked patent examination with an 80% rebate on filing fees',
  'Relaxed public procurement norms — no prior turnover or experience requirement',
  'Eligibility for the Credit Guarantee Scheme for Startups, which covers up to ₹20 crore per borrower',
  'A 90-day fast-track winding-up process under the Insolvency and Bankruptcy Code'
];

const benefits = [
  { cat: 'funding', title: 'Seed Fund', amount: 'Up to ₹30 lakh', desc: 'Grant through empanelled incubators. Not usable for construction, land, or fixed capital.' },
  { cat: 'funding', title: 'Capital Fund', amount: '₹50 crore committed', desc: 'State commits up to ₹50 crore of a ₹100 crore fund to empanelled AIFs, which must invest ₹100 crore into DPIIT-recognised MP startups.' },
  { cat: 'funding', title: 'Investment / loan assistance', amount: 'Up to ₹60 lakh lifetime', desc: '15% of each funding round received, up to ₹15 lakh per tranche, four tranches. 18% and ₹18 lakh per tranche — lifetime cap ₹72 lakh — for SC, ST, women, PwD, and transgender founders.' },
  { cat: 'funding', title: 'MMUKY startup loan', amount: 'Collateral-free', desc: 'Through the Credit Guarantee Scheme for Startups. Guarantee fee reimbursed for up to 5 years. 5% p.a. interest subsidy for up to 5 years. Stacks with investment assistance above.' },
  { cat: 'funding', title: 'State Innovation Challenge', amount: 'Up to ₹1 crore', desc: 'Grant across up to 4 phases, plus licence fee waivers and 2 years of procurement assistance.' },
  { cat: 'funding', title: 'Hackathon award', amount: 'Up to ₹5 lakh', desc: 'Per selected startup.' },
  { cat: 'reimb', title: 'Product & prototype development', amount: '100% up to ₹15 lakh', desc: 'Through government institutions.' },
  { cat: 'reimb', title: 'Patent assistance', amount: 'Up to ₹20 lakh', desc: '100% up to ₹5 lakh for a domestic filing, up to ₹20 lakh for an international filing — fees and consultancy both covered.' },
  { cat: 'reimb', title: 'Lease rental assistance', amount: '50% of rent', desc: 'Up to ₹10,000 per month, for 3 years. Covers incubator seat fees too.' },
  { cat: 'reimb', title: 'Online advertising reimbursement', amount: '100% up to ₹3 lakh', desc: 'For one year, on Amazon, Meta, Google, or Flipkart spend billed to an MP GSTIN. Once per policy period, for startups incorporated within 2 years of applying.' },
  { cat: 'reimb', title: 'Event participation', amount: '75% of cost', desc: 'Domestic events: up to ₹50,000. International events: up to ₹1.5 lakh.' },
  { cat: 'reimb', title: 'Electricity', amount: '3-year duty exemption', desc: 'Plus tariff reimbursement of ₹2 per unit for 3 years, for product startups.' },
  { cat: 'people', title: 'Entrepreneur-in-residence assistance', amount: '₹10,000 / month', desc: 'For up to 12 months, within 2 years of DPIIT recognition.' },
  { cat: 'people', title: 'Skill training', amount: '₹13,000 / employee / year', desc: 'For 3 years, up to 25 MP-domicile employees.' },
  { cat: 'people', title: 'Employment generation', amount: '₹5,000 / employee / month', desc: 'Up to 25 employees, for 3 years per employee, within 5 years of commercial production. MP-domicile hiring ratio: 50% in year 1, 75% in year 2, 90% in year 3.' }
];

const catMeta = {
  funding: { label: 'Funding', icon: IndianRupee, badge: 'bg-emerald-100 text-emerald-700' },
  reimb: { label: 'Reimbursement', icon: Receipt, badge: 'bg-blue-100 text-blue-600' },
  people: { label: 'People', icon: Users, badge: 'bg-orange-100 text-orange-600' }
};

const filters = [
  { key: 'all', label: 'All 15' },
  { key: 'funding', label: 'Funding' },
  { key: 'reimb', label: 'Reimbursements' },
  { key: 'people', label: 'People' }
];

const resources = [
  { title: 'MP Startup Policy 2022 (English)', desc: 'The full state startup policy document', url: 'https://customer-assets.emergentagent.com/job_8e73aa97-f2ea-4b76-8873-5ae0fbceef23/artifacts/s90y4jk7_Startup-Policy-2022-Eng.pdf' },
  { title: 'MP Startup Scheme 2022 (हिन्दी)', desc: 'Hindi version of the startup scheme', url: 'https://customer-assets.emergentagent.com/job_8e73aa97-f2ea-4b76-8873-5ae0fbceef23/artifacts/6ydee8p0_Startup_Scheme_2022_%28hindi%29.pdf' },
  { title: 'Gazette Notification — Amended 2022', desc: 'Official gazette of the amended policy', url: 'https://customer-assets.emergentagent.com/job_8e73aa97-f2ea-4b76-8873-5ae0fbceef23/artifacts/htw6ppay_Gazette_SPR_Amended_2022.pdf' },
  { title: 'Policy Clarification', desc: 'Official clarifications on policy provisions', url: 'https://customer-assets.emergentagent.com/job_8e73aa97-f2ea-4b76-8873-5ae0fbceef23/artifacts/n0u5io5a_Policy_Clarification.pdf' },
  { title: 'Electricity Duty Exemption Notification', desc: 'Duty exemption notification for startups', url: 'https://customer-assets.emergentagent.com/job_8e73aa97-f2ea-4b76-8873-5ae0fbceef23/artifacts/11lwn4i5_Notification_for_Electricity_Duty_Exemption_for_Startups.pdf' }
];

const eligibilityRows = [
  { step: 'Company or LLP', req: 'Private limited company, LLP, or registered partnership firm. A proprietorship qualifies for none of the steps below.' },
  { step: 'Startup India (DPIIT) recognition', req: 'Incorporated within the last 10 years. Turnover under ₹100 crore in any financial year. Working toward innovation or improvement of a product, process, or service — not a split or rebrand of an existing business.' },
  { step: 'MP Startup registration', req: 'Valid DPIIT recognition, plus a GST registration in Madhya Pradesh. Login uses the same Startup India credentials.' }
];

export default function MPStartupBenefitsPage() {
  const [scrollY, setScrollY] = useState(0);
  const [activePosition, setActivePosition] = useState(null);
  const [filter, setFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [formPosition, setFormPosition] = useState('');

  const openForm = (position = '') => {
    setFormPosition(position);
    setFormOpen(true);
  };

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'MP Startup Funding & Benefits — Snigdha & Associates';
    window.scrollTo(0, 0);
    return () => { document.title = prevTitle; };
  }, []);

  const visibleBenefits = filter === 'all' ? benefits : benefits.filter(b => b.cat === filter);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased" data-testid="mp-startup-benefits-page">
      {/* Navigation — same as main site */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrollY > 50 ? 'bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm' : 'bg-white/80 backdrop-blur-sm'
      }`}>
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="/logo.png"
                alt="Snigdha & Associates"
                className="w-10 h-10 object-contain"
              />
              <div>
                <p className="text-sm font-medium text-slate-900">Snigdha & Associates</p>
                <p className="text-xs text-slate-500">Chartered Accountants</p>
              </div>
            </Link>
            <div className="flex items-center gap-6">
              <a href="/#philosophy" className="text-sm text-slate-600 hover:text-slate-900 transition-colors hidden md:block">Philosophy</a>
              <a href="/#services" className="text-sm text-slate-600 hover:text-slate-900 transition-colors hidden md:block">India</a>
              <a href="/#contact" className="text-sm text-slate-600 hover:text-slate-900 transition-colors hidden md:block">Contact</a>
              <Link to="/login">
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 h-auto">
                  Client Portal
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-36 pb-24 md:pt-44 md:pb-32 px-6 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 mb-8">
                <Landmark className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-xs text-white/80 font-medium">MP Startup Policy 2025</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mb-6 leading-[1.12] text-white">
                Madhya Pradesh owes<br className="hidden md:block" /> your startup money.
                <span className="text-slate-400"> Most founders never climb high enough to collect it.</span>
              </h1>
              <p className="text-lg text-slate-400 max-w-2xl mb-10 leading-relaxed font-light">
                Three registrations sit between you and every grant, tax exemption, and reimbursement
                on offer. Company or LLP. Startup India recognition. MP Startup registration.
                Each rung unlocks what the last one didn't.
              </p>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <a href="#stand">
                  <Button data-testid="hero-find-your-step-btn" className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-base font-medium">
                    Find your step
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
                <Button
                  data-testid="hero-talk-to-us-btn"
                  onClick={() => openForm()}
                  variant="ghost"
                  className="text-slate-300 hover:text-white hover:bg-white/10 px-8 py-6 text-base border border-slate-600"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Talk to us
                </Button>
              </div>
            </div>

            {/* Three-step visual */}
            <div className="hidden lg:block">
              <div className="relative pl-6">
                <div className="absolute left-[2.85rem] top-8 bottom-8 w-px bg-gradient-to-b from-emerald-400/60 via-emerald-400/30 to-slate-600" />
                {[
                  { n: 3, title: 'MP Startup registration', sub: 'The cash starts here' },
                  { n: 2, title: 'Startup India (DPIIT)', sub: 'Six protections unlock here' },
                  { n: 1, title: 'Company or LLP', sub: 'The entry ticket' }
                ].map((s) => (
                  <div key={s.n} className="relative flex items-center gap-5 py-5">
                    <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center text-base font-medium border ${
                      s.n === 3 ? 'bg-emerald-500 border-emerald-400 text-white shadow-lg shadow-emerald-500/25' : 'bg-slate-800 border-slate-600 text-emerald-400'
                    }`}>
                      {s.n}
                    </div>
                    <div className="p-4 rounded-xl bg-slate-800/60 border border-slate-700 flex-1">
                      <p className="text-white text-sm font-medium">{s.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{s.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Where you stand */}
      <section id="stand" className="py-24 md:py-32 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <p className="text-sm text-emerald-600 uppercase tracking-widest mb-4 font-medium">Where You Stand</p>
            <h2 className="text-3xl md:text-4xl font-light text-slate-900">
              Where you stand<br />
              <span className="text-slate-400">today</span>
            </h2>
            <p className="text-slate-500 max-w-xl mt-4">Pick the one that's true right now.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {positions.map((pos, i) => (
              <button
                key={i}
                data-testid={`position-card-${i + 1}`}
                onClick={() => setActivePosition(activePosition === i ? null : i)}
                className={`text-left p-6 rounded-xl transition-all duration-300 border ${
                  activePosition === i
                    ? 'bg-emerald-50 border-emerald-300 shadow-sm'
                    : 'bg-slate-50 border-slate-200 hover:border-slate-300 hover:shadow-sm'
                }`}
              >
                <div className={`w-10 h-10 rounded flex items-center justify-center mb-4 transition-colors ${
                  activePosition === i ? 'bg-emerald-100' : 'bg-slate-100'
                }`}>
                  <pos.icon className={`w-5 h-5 ${activePosition === i ? 'text-emerald-600' : 'text-slate-400'}`} />
                </div>
                <p className="text-xs text-slate-400 uppercase tracking-widest mb-2">Position</p>
                <p className={`font-medium ${activePosition === i ? 'text-slate-900' : 'text-slate-600'}`}>{pos.label}</p>
              </button>
            ))}
          </div>

          {activePosition !== null && (
            <div data-testid="position-panel" className="mt-6 p-8 bg-slate-50 rounded-xl border-l-4 border border-slate-200 border-l-emerald-500">
              {positions[activePosition].paras.map((p, i) => (
                <p key={i} className="text-slate-600 leading-relaxed mb-4 max-w-3xl">{p}</p>
              ))}
              <Button
                data-testid="position-panel-cta"
                onClick={() => openForm(positions[activePosition].label)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white mt-2"
              >
                {positions[activePosition].cta}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* The ladder, rung by rung */}
      <section id="ladder" className="py-24 md:py-32 px-6 bg-slate-900">
        <div className="max-w-6xl mx-auto">
          <div className="mb-16">
            <p className="text-sm text-slate-400 uppercase tracking-widest mb-4">Three Steps</p>
            <h2 className="text-3xl md:text-4xl font-light mb-4 text-white">
              The ladder,<br />
              <span className="text-slate-400">rung by rung</span>
            </h2>
            <p className="text-slate-400 max-w-xl">
              Climb one rung and unlock funding, tax relief, and cash reimbursement
              the previous rung didn't give you.
            </p>
          </div>

          <div className="space-y-8">
            {/* Rung 1 */}
            <div className="p-8 rounded-xl bg-slate-800/50 border border-slate-700">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 rounded-full bg-slate-700 border border-slate-600 flex items-center justify-center text-emerald-400 font-medium">1</div>
                <h3 className="text-white text-xl font-medium">Company or LLP</h3>
              </div>
              <p className="text-slate-400 leading-relaxed max-w-3xl mb-3">
                A private limited company or an LLP is the entry ticket. Startup India recognition
                and MP Startup registration both require one. A proprietorship or an informal
                partnership qualifies for neither.
              </p>
              <p className="text-slate-400 leading-relaxed max-w-3xl">
                We handle incorporation start to finish — company or LLP, PAN, and GST registration.
              </p>
            </div>

            {/* Rung 2 */}
            <div className="p-8 rounded-xl bg-slate-800/50 border border-slate-700">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 rounded-full bg-slate-700 border border-slate-600 flex items-center justify-center text-emerald-400 font-medium">2</div>
                <h3 className="text-white text-xl font-medium">Startup India (DPIIT) recognition</h3>
              </div>
              <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/25 text-sm text-emerald-300 leading-relaxed max-w-3xl mb-6">
                Incorporated within the last 10 years. Turnover under ₹100 crore in any financial year.
                Working toward innovation or improvement of a product, process, or service — not a split
                or rebrand of an existing business.
              </div>
              <p className="text-slate-300 mb-4">DPIIT recognition alone unlocks:</p>
              <ul className="space-y-3 max-w-3xl">
                {dpiitUnlocks.map((u, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                    <span className="text-sm text-slate-400 leading-relaxed">{u}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Rung 3 */}
            <div className="p-8 rounded-xl bg-slate-800/50 border border-emerald-500/40">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white font-medium">3</div>
                <h3 className="text-white text-xl font-medium">MP Startup registration</h3>
              </div>
              <p className="text-slate-400 leading-relaxed max-w-3xl mb-3">
                DPIIT recognition plus a Madhya Pradesh GST registration gets a business onto
                startup.mp.gov.in, and every scheme below. Nodal department: MSME, Government of Madhya Pradesh.
              </p>
              <p className="text-slate-400 leading-relaxed max-w-3xl">
                MP Startup registration runs on Startup India credentials — there's no reaching
                rung three without rung two.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="benefits" className="py-24 md:py-32 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-sm text-emerald-600 uppercase tracking-widest mb-4 font-medium">MP Startup Policy 2025</p>
            <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">
              The full<br />
              <span className="text-slate-400">benefit table</span>
            </h2>
            <p className="text-slate-500 max-w-2xl text-sm leading-relaxed">
              Figures are from the official MP Startup Policy 2025 procedure and guidelines, dated
              24 June 2025, current as of July 2026. Apply at startup.mp.gov.in. Nodal contact:
              startup-centre@mp.gov.in, 0755-3524017.
            </p>
            <div className="mt-6 flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 max-w-2xl">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800 leading-relaxed">
                Schemes update without much notice. Confirm exact eligibility before applying —
                we track the portal weekly and flag any change.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {filters.map((f) => (
              <button
                key={f.key}
                data-testid={`benefit-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 rounded-full text-sm transition-colors border ${
                  filter === f.key
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-900'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="benefit-grid">
            {visibleBenefits.map((b, i) => {
              const meta = catMeta[b.cat];
              return (
                <div key={`${b.cat}-${i}`} className="group p-6 rounded-xl bg-slate-50 border border-slate-200 hover:border-emerald-300 hover:shadow-sm transition-all" data-testid="benefit-card">
                  <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded ${meta.badge}`}>
                    <meta.icon className="w-3 h-3" />
                    {meta.label}
                  </span>
                  <h3 className="text-slate-900 font-medium mt-4 mb-1">{b.title}</h3>
                  <p className="text-emerald-600 font-medium text-sm mb-3">{b.amount}</p>
                  <p className="text-sm text-slate-500 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Eligibility at a glance */}
      <section id="eligibility" className="py-24 md:py-32 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-sm text-emerald-600 uppercase tracking-widest mb-4 font-medium">Requirements</p>
            <h2 className="text-3xl md:text-4xl font-light text-slate-900">
              Eligibility<br />
              <span className="text-slate-400">at a glance</span>
            </h2>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            {eligibilityRows.map((row, i) => (
              <div key={i} className={`grid md:grid-cols-[280px_1fr] gap-2 md:gap-8 p-6 md:p-8 ${i > 0 ? 'border-t border-slate-200' : ''}`}>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-sm font-medium flex-shrink-0">{i + 1}</div>
                  <p className="font-medium text-slate-900">{row.step}</p>
                </div>
                <p className="text-sm text-slate-500 leading-relaxed">{row.req}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Official Resources */}
      <section id="resources" className="py-24 md:py-32 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-sm text-emerald-600 uppercase tracking-widest mb-4 font-medium">Source Documents</p>
            <h2 className="text-3xl md:text-4xl font-light text-slate-900">
              Official<br />
              <span className="text-slate-400">resources</span>
            </h2>
            <p className="text-slate-500 max-w-xl mt-4 text-sm">
              Don't take our word for it — read the government notifications yourself.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {resources.map((r, i) => (
              <a
                key={i}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`resource-link-${i + 1}`}
                className="group p-6 rounded-xl bg-slate-50 border border-slate-200 hover:border-emerald-300 hover:shadow-sm transition-all flex items-start gap-4"
              >
                <div className="w-10 h-10 rounded bg-emerald-100 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 transition-colors">
                  <FileText className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 group-hover:text-emerald-700 transition-colors">{r.title}</p>
                  <p className="text-sm text-slate-500 mt-1">{r.desc}</p>
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-600 mt-2">
                    PDF <ExternalLink className="w-3 h-3" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 bg-emerald-600">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-light mb-6 text-white">
            Tell us where<br />
            <span className="text-emerald-200">you stand</span>
          </h2>
          <p className="text-emerald-100 mb-8 max-w-xl mx-auto">
            Get a free eligibility check — tell us where you stand on the ladder
            and we'll tell you exactly what you qualify for.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              data-testid="cta-eligibility-btn"
              onClick={() => openForm()}
              className="bg-white text-emerald-700 hover:bg-emerald-50 px-8 py-6 text-base font-medium"
            >
              Get a free eligibility check
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a href="tel:+917570041003">
              <Button data-testid="cta-call-btn" variant="ghost" className="text-white hover:bg-white/10 px-8 py-6 text-base border border-emerald-400">
                <Phone className="w-4 h-4 mr-2" />
                Call +91 75 7004 1003
              </Button>
            </a>
          </div>
        </div>
      </section>

      <EligibilityDialog open={formOpen} onOpenChange={setFormOpen} defaultPosition={formPosition} />

      {/* Footer — same as main site */}
      <footer className="border-t border-slate-200 py-12 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img
                src="/logo.png"
                alt="Snigdha & Associates"
                className="w-8 h-8 object-contain"
              />
              <div>
                <p className="text-sm text-slate-700">Snigdha & Associates</p>
                <p className="text-xs text-slate-400">Chartered Accountants</p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <Link to="/mp-startup-benefits" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">MP Startup Funding & Benefits</Link>
              <Link to="/terms" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">Terms</Link>
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-slate-900 transition-colors">Privacy</Link>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-100">
                <Shield className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-xs text-emerald-700">ISO 27001</span>
              </div>
              <p className="text-xs text-slate-400">© 2026 All rights reserved</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
